import { Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import {
  Check,
  ArrowRight,
  ChevronRight,
  Calculator,
  Receipt,
  ShieldCheck,
  TrendingUp,
  Eye,
  Wallet,
  ClipboardCheck,
  HeartHandshake,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import {
  CashDiscountHeroIllustration,
  DualPricingIllustration,
  GrowthIllustration,
  ProcessIllustration,
} from "@/components/illustrations/FintechIllustrations";

/* ────────────────────────────────────────────────────────────
   Reusable illustration frame (matches FreePos / Savings)
   ──────────────────────────────────────────────────────────── */
function IllustrationFrame({ Component, side = "left" }) {
  return (
    <div className="relative">
      <div
        aria-hidden="true"
        className="absolute -inset-4 rounded-[40px] pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 70% 70% at ${side === "left" ? "30%" : "70%"} 50%, rgba(0,114,255,0.18) 0%, transparent 70%)`,
        }}
      />
      <ParticleField
        density={24}
        connectDistance={110}
        className="absolute inset-0 w-full h-full rounded-[40px]"
      />
      <Component className="relative w-full h-auto max-w-[540px] mx-auto drop-shadow-[0_30px_60px_rgba(6,26,58,0.18)]" />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   UNIFIED HERO — matches FreePos / Savings Analysis
   ──────────────────────────────────────────────────────────── */
function UnifiedHero() {
  return (
    <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
      <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
      <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
      <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
      <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
      <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
      <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

      <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

      <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
        <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8" data-testid="cash-discount-breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/services" className="hover:text-white">Services</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white/80">Cash Discount</span>
        </nav>

        <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                Cash Discount Program
              </div>
              <h1 className="font-display-tight text-[2.5rem] sm:text-[3.25rem] lg:text-[4.25rem] xl:text-[4.5rem] font-extrabold leading-[1.05] text-white uppercase tracking-[-0.03em]">
                Cash Discount <span className="text-[var(--swipe-accent)]">Program</span>
              </h1>
              <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                Reduce monthly processing costs with a clear, professional cash discount setup.
                Swipetronics helps businesses offer transparent payment options while helping
                owners lower their effective processing expense.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/savings-analysis" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid="cash-discount-hero-savings">
                  Request a Free Savings Analysis <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/get-started" className="btn-outline-light text-[15px] py-3.5 px-7" data-testid="cash-discount-hero-get-started">
                  Get Started
                </Link>
              </div>
            </div>
          </Reveal>

          <Reveal variant="slide-right" delay={120}>
            <div className="relative">
              <CashDiscountHeroIllustration transparent className="relative w-full h-auto max-w-[560px] mx-auto" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   PAGE
   ──────────────────────────────────────────────────────────── */
export default function CashDiscount() {
  return (
    <div data-testid="cash-discount-page">
      <Seo {...getSeoForPath("/cash-discount")} />
      <UnifiedHero />

      {/* SECTION 1 — text LEFT, illustration RIGHT */}
      <section className="section bg-white">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-12 lg:gap-16 items-center">
            <div>
              <Reveal>
                <div className="overline mb-4">How Cash Discount Works</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                  Clear pricing for your customers — and a lower effective rate for you.
                </h2>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 text-[17px] text-slate-600 leading-[1.8] max-w-xl">
                  Most businesses absorb card processing fees every month as a hidden cost.
                  A cash discount program makes those costs more transparent by showing clear
                  cash and card pricing at checkout. Customers still choose how they want to pay,
                  while the business may reduce its monthly processing expense.
                </p>
              </Reveal>
              <Reveal delay={240}>
                <ul className="mt-7 space-y-3.5" data-testid="cash-discount-how-list">
                  {[
                    "Two clear prices posted at checkout — cash and card",
                    "Compliant signage and receipts configured at setup",
                    "Works on most modern POS systems and terminals",
                    "Customer always keeps the choice — nothing hidden",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <span className="mt-1 w-5 h-5 rounded-full bg-[var(--swipe-accent)] text-white flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3.5} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <Reveal variant="slide-right" delay={120}>
              <IllustrationFrame Component={DualPricingIllustration} side="right" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 2 — illustration LEFT, text RIGHT */}
      <section className="section bg-[var(--swipe-accent-soft)]/40 border-y border-[var(--swipe-accent)]/15">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1fr,1.05fr] gap-12 lg:gap-16 items-center">
            <Reveal variant="slide-left">
              <IllustrationFrame Component={GrowthIllustration} side="left" />
            </Reveal>
            <div>
              <Reveal>
                <div className="overline mb-4">Monthly Savings</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                  How it can save businesses money every month.
                </h2>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 text-[17px] text-slate-600 leading-[1.8] max-w-xl">
                  Processing fees can quietly reduce profit every month. A properly structured
                  cash discount program may help lower a business&apos;s effective processing expense,
                  improve cash flow, and help owners keep more of each sale — without changing
                  how customers shop.
                </p>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-7 grid sm:grid-cols-2 gap-3.5">
                  {[
                    { icon: TrendingUp, t: "Lower effective rate", d: "Card fees become an explicit line item." },
                    { icon: Wallet, t: "Better cash flow", d: "Keep more of each sale instead of absorbing fees." },
                    { icon: Eye, t: "More transparent", d: "Customers see exactly what each option costs." },
                    { icon: Calculator, t: "Predictable monthly cost", d: "Cleaner statements, fewer surprises." },
                  ].map((b) => {
                    const Icon = b.icon;
                    return (
                      <div
                        key={b.t}
                        className="rounded-xl border border-[var(--swipe-accent)]/15 bg-white px-4 py-4 flex items-start gap-3"
                      >
                        <div className="w-10 h-10 rounded-lg bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5" strokeWidth={2.25} />
                        </div>
                        <div>
                          <div className="font-display text-[14px] font-bold text-[var(--swipe-navy)]">{b.t}</div>
                          <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">{b.d}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — text LEFT, illustration RIGHT */}
      <section className="section bg-white">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-12 lg:gap-16 items-center">
            <div>
              <Reveal>
                <div className="overline mb-4">Set Up The Right Way</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                  Professional, transparent, and set up the right way.
                </h2>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 text-[17px] text-slate-600 leading-[1.8] max-w-xl">
                  Swipetronics helps business owners understand if cash discount is a good fit,
                  explains the setup clearly, and configures everything customer-facing — pricing
                  displays, receipts, signage, and POS prompts — with ongoing support after launch.
                </p>
              </Reveal>
              <Reveal delay={240}>
                <ul className="mt-7 space-y-3.5">
                  {[
                    { icon: ClipboardCheck, t: "Eligibility review", d: "We check if a cash discount setup is right for your business and industry." },
                    { icon: Receipt, t: "Receipts & signage", d: "Configured to match program requirements and stay clear for customers." },
                    { icon: ShieldCheck, t: "Compliant POS setup", d: "Cash and card prompts configured on your POS or terminal." },
                    { icon: HeartHandshake, t: "Real support", d: "A real person at Swipetronics walks you through the entire setup." },
                  ].map((b) => {
                    const Icon = b.icon;
                    return (
                      <li key={b.t} className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[var(--swipe-accent)] text-white flex items-center justify-center shrink-0">
                          <Icon className="w-4.5 h-4.5" strokeWidth={2.25} />
                        </div>
                        <div>
                          <div className="font-display text-[15px] font-bold text-[var(--swipe-navy)]">{b.t}</div>
                          <p className="text-[14px] text-slate-600 leading-relaxed">{b.d}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </Reveal>
            </div>
            <Reveal variant="slide-right" delay={120}>
              <IllustrationFrame Component={ProcessIllustration} side="right" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* SAMPLE RECEIPT + BENEFITS — polished side-by-side */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden">
        <div className="absolute inset-0 grid-faint opacity-25 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(0,114,255,0.22) 0%, transparent 65%)" }} />

        <div className="container-swipe relative py-20 lg:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="overline mb-3 inline-flex justify-center" style={{ color: "#7DB6FF" }}>
                What it looks like in practice
              </div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold leading-[1.1] text-white">
                A simple, transparent customer experience.
              </h2>
              <p className="mt-5 text-[16px] text-white/70 leading-[1.75] max-w-[780px] mx-auto">
                Two clearly posted prices. The customer chooses. The receipt shows exactly what
                they paid and why — no surprises.
              </p>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-[1fr,1.1fr] gap-10 lg:gap-14 items-center">
            {/* Sample receipt card */}
            <Reveal variant="slide-left">
              <div
                className="relative bg-white rounded-2xl p-8 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.45)] border border-white/10 mx-auto max-w-[420px]"
                data-testid="cash-discount-sample-receipt"
              >
                <div
                  aria-hidden="true"
                  className="absolute -top-3 left-0 right-0 h-3 rounded-t-2xl"
                  style={{ background: "linear-gradient(90deg, #0072FF 0%, #3A8EFF 100%)" }}
                />
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Sample Receipt</div>
                    <div className="mt-1 font-display text-[18px] font-bold text-[var(--swipe-navy)]">Corner Café</div>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-[var(--swipe-accent-soft)] flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-[var(--swipe-accent)]" strokeWidth={2.25} />
                  </div>
                </div>

                <div className="mt-6 space-y-2.5 font-mono text-[13px] text-slate-700">
                  <div className="flex justify-between"><span>Burger Combo</span><span>$11.50</span></div>
                  <div className="flex justify-between"><span>Soft Drink</span><span>$2.50</span></div>
                  <div className="border-t border-dashed border-slate-200 pt-3 mt-3 flex justify-between font-bold text-[var(--swipe-navy)]">
                    <span>Subtotal (Cash)</span><span>$14.00</span>
                  </div>
                  <div className="flex justify-between text-[var(--swipe-accent)] font-bold bg-[var(--swipe-accent-soft)] -mx-3 px-3 py-2 rounded-lg">
                    <span>Card Price</span><span>$14.50</span>
                  </div>
                  <div className="flex justify-between text-[11px] text-slate-500 pt-2">
                    <span>Customer chose:</span><span className="font-bold text-[var(--swipe-navy)]">Card</span>
                  </div>
                </div>

                <p className="mt-6 text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-4">
                  Receipts, signage, and customer-facing prompts are configured at setup.
                  Exact wording depends on program type and applicable rules.
                </p>
              </div>
            </Reveal>

            {/* Why businesses choose it */}
            <div>
              <Reveal>
                <div className="overline mb-4" style={{ color: "#7DB6FF" }}>Why businesses choose it</div>
              </Reveal>
              <Reveal delay={80}>
                <h3 className="font-display-tight text-[1.5rem] sm:text-[1.85rem] font-extrabold text-white leading-[1.18] max-w-md">
                  The benefits of a properly structured program.
                </h3>
              </Reveal>
              <Reveal delay={160}>
                <div className="mt-7 grid sm:grid-cols-1 gap-3">
                  {[
                    { icon: Calculator, t: "Reduced effective rate", d: "Processing becomes an explicit line item — and may lower your effective rate." },
                    { icon: Receipt, t: "Transparent for customers", d: "No hidden surcharge surprises. Customers see both prices clearly." },
                    { icon: ShieldCheck, t: "Compliant setup", d: "POS prompts, signage, and receipts configured to match program rules." },
                  ].map((b) => {
                    const Icon = b.icon;
                    return (
                      <div
                        key={b.t}
                        className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur px-5 py-4 flex items-start gap-4 hover:border-[var(--swipe-accent)]/50 transition-colors"
                        data-testid={`cash-discount-benefit-${b.t.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-[var(--swipe-accent)]/15 text-[var(--swipe-accent)]">
                          <Icon className="w-5 h-5" strokeWidth={2.25} />
                        </div>
                        <div>
                          <div className="font-display text-[15px] font-bold text-white">{b.t}</div>
                          <p className="mt-1 text-[13px] text-white/65 leading-relaxed">{b.d}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* IMPORTANT NOTE — polished */}
      <section className="section container-swipe">
        <Reveal>
          <div className="relative rounded-2xl overflow-hidden border border-[var(--swipe-accent)]/15 bg-[var(--swipe-accent-soft)]/60 p-8 lg:p-10">
            <div
              aria-hidden="true"
              className="absolute top-0 left-0 right-0 h-[3px]"
              style={{ background: "linear-gradient(90deg, #0072FF 0%, #3A8EFF 60%, #061A3A 100%)" }}
            />
            <div className="grid sm:grid-cols-[auto,1fr] gap-5 items-start">
              <div className="w-12 h-12 rounded-xl bg-[var(--swipe-accent)] text-white flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6" strokeWidth={2.25} />
              </div>
              <div>
                <div className="overline mb-2">Important note</div>
                <h3 className="font-display text-[18px] font-bold text-[var(--swipe-navy)] mb-2">
                  Program availability varies.
                </h3>
                <p className="text-[15px] text-slate-700 leading-[1.8]">
                  Program availability depends on business type, card brand rules, state
                  requirements, and approval. Swipetronics will help confirm what setup is
                  appropriate for your business during onboarding.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* STRONGER BOTTOM CTA */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-t border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 70% at 80% 50%, rgba(0,114,255,0.28) 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(120px)" }} />
        <ParticleField density={28} connectDistance={120} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative py-20 lg:py-24">
          <div className="grid lg:grid-cols-[1.3fr,1fr] gap-10 lg:gap-14 items-center">
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Free, no-pressure review
                </div>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.25rem] font-extrabold leading-[1.08] text-white">
                  See if cash discount is right for your business.
                </h2>
                <p className="mt-6 text-[17px] text-white/75 leading-[1.75] max-w-2xl">
                  Every business is different. Swipetronics can review your current setup, explain
                  how a cash discount program works, and help you understand whether it may be a
                  good fit for your business.
                </p>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="flex flex-col gap-3 lg:items-end">
                <Link
                  to="/savings-analysis?utm_source=cash-discount&utm_medium=page&utm_campaign=cash_discount_cta"
                  className="btn-primary-swipe text-[15px] py-3.5 px-7 w-full sm:w-auto justify-center"
                  data-testid="cash-discount-cta-savings"
                >
                  Request a Free Savings Analysis <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/get-started"
                  className="btn-outline-light text-[15px] py-3.5 px-7 w-full sm:w-auto justify-center"
                  data-testid="cash-discount-cta-get-started"
                >
                  Get Started
                </Link>
                <p className="mt-2 text-[12px] text-white/45 max-w-[280px] leading-relaxed lg:text-right">
                  No commitment. A real person reviews your setup and gives you an honest answer.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
